import { createRouter, createWebHistory } from 'vue-router';
import {
  BookmarkIcon,
  BotIcon,
  FilePlus2Icon,
  FileTextIcon,
  LineChartIcon,
  ListChecksIcon,
  SparklesIcon,
  TargetIcon,
  TrophyIcon,
} from 'lucide-vue-next';
import DashNav from '../components/DashNav.vue';
import { COMING_SOON_FEATURES } from '@/config/featureAvailability';
import { PAYWALL_SOURCE } from '@/observability/events';
import { trackPaywallIntent } from '@/observability/paywall';
import { applyRouteSeo } from '@/composables/useBlogSeo';
import { translateLegacyTopicQuery } from '@/utils/topicFilterState';

// Pro features advertised on the subscription screens are derived from the
// routes that carry a `meta.proFeature`. Colocating the copy + icon with the
// route keeps the "what you unlock" list in lockstep with what's actually
// shipped and navigable - add a module, it shows up; remove it, it's gone.
// `getProFeatures()` (src/config/proFeatures.js) reads these back out, ordered.
import OrganisationSidebarView from '../components/organisation/OrganisationSidebarView.vue';

const routes = [
  {
    // Unauthenticated phone upload page for Sorty files-mode submissions.
    // Lives outside DashNav and does not touch the auth store - the only
    // credential is the signed token in the URL path.
    path: '/upload/:token',
    name: 'sorty-phone-upload',
    component: () => import('../views/SortyUploadView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    // Unauthenticated phone upload page for the worksheet-builder QR handoff.
    // Like the Sorty page above, it lives outside DashNav and the only
    // credential is the signed token in the URL path.
    path: '/worksheet-upload/:token',
    name: 'worksheet-phone-upload',
    component: () => import('../views/Organisation/WorksheetUploadView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/',
    name: 'home-lp',
    component: () => import('../views/HomePageView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/teachers',
    name: 'teacher-landing',
    component: () => import('../views/Organisation/TeacherLandingPage.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/our-partners',
    name: 'partners-lp',
    component: () => import('../views/OurPartnersView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/register',
    name: 'register-page',
    redirect: (to) => ({
      name: 'auth-register',
      query: to.query,
      hash: to.hash,
    }),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/blogs',
    name: 'blogs',
    component: () => import('../views/BlogsView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/webinars',
    name: 'webinars',
    component: () => import('../views/WebinarsView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/blogs/:slug',
    name: 'blog-detail',
    component: () => import('../views/BlogDetailView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/terms-of-service',
    name: 'terms-of-service',
    component: () => import('../views/TermsOfServiceView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import('../views/PrivacyPolicyView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/cookie-policy',
    name: 'cookie-policy',
    component: () => import('../views/CookiePolicyView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    // PIX-161 / S6: unauthenticated phone upload page. Lives outside
    // DashNav and intentionally does not touch the auth store - the only
    // credential is the signed token in the URL path.
    path: '/upload/:token',
    name: 'sorty-phone-upload',
    component: () => import('../views/SortyUploadView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  // Dev-only routes - stripped from production builds by Vite's dead-code elimination
  ...(import.meta.env.DEV
    ? [
        {
          path: '/dev/latex-test',
          name: 'latex-test',
          component: () => import('../views/LatexTestView.vue'),
          meta: { requiresAuth: false },
        },
      ]
    : []),
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('../views/Auth/LoginView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/auth/register',
    name: 'auth-register',
    component: () => import('../views/Auth/RegisterView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/auth/choose-handle',
    name: 'choose-handle',
    component: () => import('../views/Auth/HandlePickerView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/auth/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/Auth/ForgotPasswordView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/auth/reset-password/:token',
    name: 'reset-password-with-token',
    component: () => import('../views/Auth/PasswordResetConfirm.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    // Standalone checkout (no dashboard shell) - the pay-now signup handoff
    // and direct upgrade links land here.
    path: '/subscribe',
    name: 'subscribe-standalone',
    component: () => import('../views/SubscriptionCheckoutView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/payment-success',
    name: 'payment-success',
    component: () => import('../views/PaymentCompletedView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/dashboard',
    component: DashNav,
    children: [
      {
        path: '', // default route
        name: 'home',
        component: () => import('../views/HomeView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'subjects',
        name: 'legacy-subjects',
        redirect: { name: 'home' },
      },
      {
        path: 'sorty-ai',
        name: 'sorty-ai',
        component: () => import('../views/SortyAIView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
          proFeature: {
            order: 4,
            title: 'sorty ai',
            subtitle: 'Share your screen to ask any doubt',
            icon: BotIcon,
            iconClass: 'bg-blue-50 text-blue-600',
          },
        },
      },
      {
        path: 'subjects/:subjectUuid',
        name: 'subject',
        component: () => import('../views/SubjectDashboardView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
        },
      },
      // Concept Capsules is advertised as coming soon, but its routes stay
      // unavailable until the feature is ready to launch.
      // {
      //   path: 'concept-capsules',
      //   name: 'concept-capsules',
      //   component: () => import('../views/ConceptCapsulesView.vue'),
      //   meta: {
      //     requiresAuth: true,
      //     requiresSubscription: true,
      //     proFeature: {
      //       order: 6,
      //       title: 'Concept Capsules',
      //       subtitle: 'Bite-sized concept refreshers',
      //       icon: LayersIcon,
      //       iconClass: 'bg-indigo-50 text-indigo-600',
      //     },
      //   },
      // },
      // {
      //   path: 'concept-capsules/session/:sessionUuid',
      //   name: 'concept-capsule-session',
      //   component: () => import('../views/ConceptCapsuleReaderView.vue'),
      //   meta: {
      //     requiresAuth: true,
      //     requiresSubscription: true,
      //   },
      // },
      {
        path: 'exam-builder',
        name: 'exam-builder',
        component: () => import('../views/ExamBuilderListView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
          proFeature: {
            order: 5,
            title: 'Exam Builder',
            subtitle: 'Unlimited exam-like question generation',
            icon: FilePlus2Icon,
            iconClass: 'bg-blue-50 text-blue-600',
          },
        },
      },
      {
        path: 'exam-builder/:examUuid',
        name: 'exam-builder-detail',
        component: () => import('../views/ExamDetailView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
        },
      },
      {
        path: 'exam-builder/:examUuid/question/:questionUuid',
        name: 'exam-builder-question-detail',
        component: () => import('../views/ExamQuestionView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
        },
      },
      {
        path: 'insights',
        name: 'insights',
        component: () => import('../views/InsightsView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
          proFeature: {
            order: 8,
            title: 'Personalised Insights',
            subtitle: 'Track progress & weak areas',
            icon: LineChartIcon,
            iconClass: 'bg-blue-50 text-blue-600',
          },
        },
      },
      {
        path: 'my-organisation',
        name: 'my-organisation',
        component: () => import('../views/MyOrganisationView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'my-worksheets',
        name: 'my-worksheets',
        component: () => import('../views/MyWorksheetsView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'my-worksheets/:assignmentUuid/report',
        name: 'my-worksheet-report',
        component: () => import('../views/MyWorksheetReportView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'my-worksheets/:assignmentUuid/report/full',
        name: 'my-worksheet-report-full',
        component: () => import('../views/MyWorksheetReportFullView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'predicted-papers',
        name: 'predicted-papers',
        component: () => import('../views/PredictedPapersFormView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
          comingSoon: true,
          proFeature: {
            order: 7,
            title: COMING_SOON_FEATURES.predictedBoardExam.title,
            subtitle: 'Practice what is likely to come up',
            icon: TargetIcon,
            iconClass: 'bg-gray-100 text-gray-400',
            comingSoon: true,
          },
        },
      },
      {
        path: 'predicted-papers/:uuid',
        name: 'predicted-papers-detail',
        component: () => import('../views/PredictedPaperDetailView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
          comingSoon: true,
        },
      },
      {
        path: 'saved-questions',
        name: 'saved-questions',
        beforeEnter: translateLegacyTopicRoute,
        component: () => import('../views/SavedQuestionsListView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
          proFeature: {
            order: 9,
            title: 'Save Questions',
            subtitle: 'Bookmark for later revision',
            icon: BookmarkIcon,
            iconClass: 'bg-blue-50 text-blue-600',
          },
        },
      },
      {
        path: 'topics',
        name: 'topic-wise',
        beforeEnter: translateLegacyTopicRoute,
        component: () => import('../views/TopicWiseListView.vue'),
        meta: {
          requiresAuth: true,
          proFeature: {
            order: 2,
            title: 'Topic Wise Questions',
            subtitle: 'Practice by syllabus topic',
            icon: ListChecksIcon,
            iconClass: 'bg-violet-50 text-violet-600',
          },
        },
      },
      {
        path: 'recommended',
        name: 'recommended',
        beforeEnter: translateLegacyTopicRoute,
        component: () => import('../views/RecommendListView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
          proFeature: {
            order: 3,
            title: 'Recommended Questions',
            subtitle: 'Smart picks based on your progress',
            icon: SparklesIcon,
            iconClass: 'bg-blue-50 text-blue-600',
          },
        },
      },
      {
        path: 'papers',
        name: 'past-papers',
        component: () => import('../views/PapersListView.vue'),
        meta: {
          requiresAuth: true,
          proFeature: {
            order: 1,
            title: 'Practice Paper Questions',
            subtitle: 'Markschemes & Sorted Solutions with explanations',
            icon: FileTextIcon,
            iconClass: 'bg-blue-50 text-blue-600',
          },
        },
      },
      {
        path: 'papers/:paperUuid',
        name: 'past-papers-detail',
        component: () => import('../views/PaperDetailView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/:viewCategory(topic-wise|recommended|exam-builder|past-papers)/question/:questionUuid',
        name: 'question-detail',
        component: () => import('../views/QuestionView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/:viewCategory(my-worksheets)/question/:questionUuid',
        name: 'student-worksheet-question-detail',
        component: () => import('../views/MyWorksheetQuestionView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: '/past-papers/:paperUuid/question/:questionUuid',
        name: 'past-papers-question-detail',
        component: () => import('../views/QuestionView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('../views/ProfileView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'profile/change-password',
        name: 'change-password',
        component: () => import('../views/ChangePasswordView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'profile/personal-information',
        name: 'personal-information',
        component: () => import('../views/PersonalInformationView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'leaderboard',
        name: 'leaderboard',
        component: () => import('../views/LeaderboardView.vue'),
        meta: {
          requiresAuth: true,
          requiresSubscription: true,
          proFeature: {
            order: 10,
            title: 'Leaderboard',
            subtitle: 'Compete and stay motivated',
            icon: TrophyIcon,
            iconClass: 'bg-amber-50 text-amber-600',
          },
        },
      },
      {
        path: 'subscribe',
        name: 'subscription-checkout',
        component: () => import('../views/SubscriptionCheckoutView.vue'),
        props: { standalone: false },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'settings/subscription',
        name: 'settings-subscription',
        component: () => import('../views/SubscriptionSettingsView.vue'),
      },
      {
        path: 'my-worksheets/paper/:paperUuid',
        name: 'upload-paper',
        component: () => import('../views/MyWorksheetUploadView.vue'),
        meta: {
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/register-organisation',
    name: 'register-organisation',
    component: () =>
      import('../views/Organisation/RegisterOrganisationView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/organisation/login',
    name: 'organisation-login',
    component: () => import('../views/Organisation/OrganisationLoginView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/org',
    component: OrganisationSidebarView,
    meta: { requiresOrgAuth: true },
    children: [
      {
        path: '',
        redirect: { name: 'org-classroom' },
      },
      {
        path: 'worksheet-builder',
        name: 'org-worksheet-builder',
        component: () =>
          import('../views/Organisation/WorksheetBuilderDashboardView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'worksheet-builder/add-from-repository',
        name: 'org-worksheet-builder-add-from-repo',
        component: () =>
          import('../views/Organisation/WorksheetBuilderAddFromRepo.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'worksheet-builder/upload',
        name: 'org-worksheet-builder-upload',
        component: () =>
          import('../views/Organisation/WorksheetBuilderUpload.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'worksheet-builder/:worksheetUuid/view',
        name: 'org-worksheet-builder-view',
        component: () =>
          import('../views/Organisation/WorksheetBuilderView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'worksheet-builder/using-sorty',
        name: 'org-worksheet-builder-using-sorty',
        component: () =>
          import('../views/Organisation/WorksheetBuilderUsingSorty.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'worksheet-builder/saved',
        name: 'org-worksheet-builder-saved',
        component: () =>
          import('../views/Organisation/WorksheetBuilderSavedView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'worksheet-builder/:worksheetUuid/assign',
        name: 'org-worksheet-builder-assign-saved',
        component: () =>
          import('../views/Organisation/WorksheetBuilderAssignSavedView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'worksheet-builder/upload/:worksheetUuid/assign',
        name: 'org-worksheet-builder-upload-assign-saved',
        component: () =>
          import('../views/Organisation/WorksheetBuilderAssignSavedView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'worksheet-builder/add-from-repository/:worksheetUuid/assign',
        name: 'org-worksheet-builder-repo-assign-saved',
        component: () =>
          import('../views/Organisation/WorksheetBuilderAssignSavedView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'worksheet-builder/using-sorty/:worksheetUuid/assign',
        name: 'org-worksheet-builder-sorty-assign-saved',
        component: () =>
          import('../views/Organisation/WorksheetBuilderAssignSavedView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'manage-organisation',
        name: 'org-manage-organisation',
        component: () =>
          import('../views/Organisation/ManageOrganisationView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'classroom',
        name: 'org-classroom',
        component: () => import('../views/Organisation/ClassroomView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'classroom/students/:studentUuid',
        name: 'org-classroom-student-detail',
        component: () => import('../views/Organisation/StudentDetailView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'classroom/students/:studentUuid/insights',
        name: 'org-classroom-student-insights',
        component: () =>
          import('../views/Organisation/StudentInsightsView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'classroom/:boardName/:subjectsUuid?',
        name: 'org-classroom-subject-wise-insights',
        component: () =>
          import('../views/Organisation/SubjectWiseInsightsView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'grade-for-me',
        name: 'org-grade-for-me',
        component: () => import('../views/Organisation/GradeForMeView.vue'),
        meta: { requiresOrgAuth: true },
      },
      {
        path: 'grade-for-me/review/:submissionUuid',
        name: 'org-grade-for-me-review',
        component: () =>
          import('../views/Organisation/GradeForMePerQuestionReview.vue'),
        meta: { requiresOrgAuth: true },
      },
    ],
  },
  {
    path: '/organisation/forgot-password',
    name: 'organisation-forgot-password',
    component: () =>
      import('../views/Organisation/OrganisationForgotPasswordView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: '/organisation/reset-password/:token',
    name: 'organisation-reset-password',
    component: () =>
      import('../views/Organisation/OrganisationPasswordResetConfirm.vue'),
    meta: {
      requiresAuth: false,
    },
  },
  {
    // Set the Teacher/Admin password forcefully, when first login
    path: '/organisation/set-new-password/:token',
    name: 'set-new-password',
    component: () =>
      import('../views/Organisation/OrganisationForcePasswordResetView.vue'),
    meta: {
      requiresAuth: false,
    },
  },
];

// Links from before PIX-773 carry `subtopics=`, which now names the topics
// themselves. Rewritten before the view mounts so its first request is right.
function translateLegacyTopicRoute(to) {
  const query = translateLegacyTopicQuery(to.query);
  return query ? { ...to, query, replace: true } : true;
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Back / Forward button support
    if (savedPosition) {
      return savedPosition;
    }

    // hash scroll
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
        top: 80,
      };
    }

    // Default scroll to top
    return { top: 0, behavior: 'smooth' };
  },
});

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore();
  if (
    (to.name === 'login' || to.name === 'auth-register') &&
    auth.isAuthenticated
  ) {
    await auth.fetchUser();
    return next({ name: 'home' });
  }
  // Panel requests name the organisation they act on via a header, so the
  // membership list - and the selection derived from it - has to resolve before
  // a view fires its first request.
  if (to.matched.some((record) => record.meta.requiresOrgAuth)) {
    const orgAuth = useAuthOrganisationStore();
    if (orgAuth.isAuthenticated && !orgAuth.organisations.length) {
      await orgAuth.fetchOrganisationUser();
    }
  }
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!auth.isAuthenticated) {
      return next({ name: 'login' });
    }
    // Handle picker (PIX-457): anyone without a confirmed handle picks one
    // once, before anything else. The money path (pay-now checkout and the
    // payment redirect) is exempt so a purchase is never interrupted.
    const HANDLE_EXEMPT = [
      'choose-handle',
      'subscription-checkout',
      'subscribe-standalone',
      'payment-success',
    ];
    if (!HANDLE_EXEMPT.includes(to.name)) {
      if (!auth.user) await auth.fetchUser();
      if (auth.user?.handle_is_auto_generated === true) {
        return next({
          name: 'choose-handle',
          query: { redirect: to.fullPath },
        });
      }
    }
    // Coming-soon modules remain visible in product navigation and feature
    // lists, but cannot be opened via a saved or manually entered URL.
    if (to.meta?.comingSoon) {
      return next({ name: 'home' });
    }
    // PIX-241: Pro modules require an active subscription. Guarding here (not
    // just in the sidebar click handler) covers direct-URL access, so an
    // unsubscribed user lands on the checkout "unlock" screen instead of an
    // empty, permanently-403ing page. The board from ?boards= pre-selects it.
    if (to.meta?.requiresSubscription) {
      if (!auth.user) await auth.fetchUser();
      if (!auth.isSubscribed) {
        // Reaching a Pro module by URL rather than by clicking a locked
        // menu item is a stronger statement of intent - they knew where
        // they were going. Worth separating from a sidebar bounce.
        trackPaywallIntent(to.name, PAYWALL_SOURCE.DIRECT_URL);
        const board = to.query?.boards;
        return next({
          name: 'subscription-checkout',
          query: board ? { board } : {},
        });
      }
    }
    return next();
  } else {
    return next();
  }
});

// Apply metadata only after successful navigation, including auth redirects.
router.afterEach((to, from, failure) => {
  if (!failure) applyRouteSeo(to, from);
});

export default router;
